"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ THE CHORE REGISTRY. Which clock-driven work exists, and how often.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * BRS §3.11.7 asks for a job monitor with *"last-run status, failures, and manual re-run"* and names
 * five chores. The ledger it reads (`chore_runs`, 0035) can answer what **ran**; it cannot answer
 * what **exists** — a chore that has never run, or whose registration a deploy dropped, has no row,
 * so a monitor driven off the ledger alone silently omits exactly the chore somebody is looking for.
 *
 * ⚠️ This is `features/registry.ts`'s argument a second time, and 0034's header carries the
 * post-mortem it comes from: the predecessor made the registry a **table**, made a foreign key point
 * at it, and populated it with nothing. What a database keeps is what only it can — here, the runs.
 * What a deploy knows is which chores exist, and a deploy is the thing that changes that.
 *
 * ⚠️⚠️ **`everyMs` LIVES HERE AND NOWHERE ELSE.** It was a literal in `jobs.module.ts`, which is
 * where it belongs if nothing else needs it — but the monitor needs it, to say *"last ran 47 minutes
 * ago"* about an hourly chore and mean something by it. Two copies of a cadence is a monitor that
 * calls a healthy chore late, so the module reads these values rather than restating them.
 *
 * ⚠️ **A chore MUST be idempotent**, and that is a precondition of this file rather than a remark.
 * `worker.ts` states it for the timer — nothing guarantees a chore runs once, because a restart, a
 * second API instance or a clock change can all repeat it — and the Control Panel's **Run now**
 * makes it load-bearing: an operator can fire any of these at any moment, on top of a scheduled run
 * already in flight. A chore that is not safe to run twice may not be listed here.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CHORE_KEYS = exports.ALERT_SCAN_BATCH = exports.CHORES = exports.FX_SYNC = exports.INTEGRATION_MAIL = exports.FINANCE_MAIL = exports.LOGGING_MAIL = exports.INVITE_REMINDER = exports.ALERT_DIGEST = exports.CONTRACT_WATCH = exports.REQUEST_MAIL = exports.EMAIL_AVATARS = exports.ALERT_SCAN = exports.LEAVE_CARRY_OVER = exports.INTEGRATION_POLL = exports.INTEGRATION_OUTBOUND = exports.INTEGRATION_OVERWATCH = exports.BLOB_SWEEP = exports.TRIAL_EXPIRY = exports.DUE_SWEEP_PRUNE = exports.DUE_SWEEP = void 0;
exports.isChoreKey = isChoreKey;
exports.choreDefinition = choreDefinition;
exports.choreCadence = choreCadence;
exports.DUE_SWEEP = 'due-sweep';
exports.DUE_SWEEP_PRUNE = 'due-sweep-prune';
exports.TRIAL_EXPIRY = 'trial-expiry';
exports.BLOB_SWEEP = 'blob-sweep';
exports.INTEGRATION_OVERWATCH = 'integration-overwatch';
exports.INTEGRATION_OUTBOUND = 'integration-outbound';
exports.INTEGRATION_POLL = 'integration-poll';
exports.LEAVE_CARRY_OVER = 'leave-carry-over';
exports.ALERT_SCAN = 'alert-scan';
exports.EMAIL_AVATARS = 'email-avatars';
exports.REQUEST_MAIL = 'request-mail';
exports.CONTRACT_WATCH = 'contract-watch';
exports.ALERT_DIGEST = 'alert-digest';
exports.INVITE_REMINDER = 'invite-reminder';
exports.LOGGING_MAIL = 'logging-mail';
exports.FINANCE_MAIL = 'finance-mail';
exports.INTEGRATION_MAIL = 'integration-mail';
exports.FX_SYNC = 'fx-sync';
exports.CHORES = [
    {
        key: exports.FX_SYNC,
        description: "Pulls each day's ECB exchange rates (Frankfurter) into every workspace that takes the bank's " +
            'rates, once a day after 17:00 Berlin time, and fills in any day it missed (0230, FX-1). If ' +
            'this stops, money in other currencies converts at the last rate on file.',
        /** ⚠️ Hourly; the function it reads leaves out a workspace already synced today. */
        everyMs: 60 * 60_000,
    },
    {
        key: exports.ALERT_SCAN,
        description: 'Re-runs the 26 data-quality rules over the projects due a scan, oldest first. If this ' +
            'stops, RISK freezes at whatever it last said and three of the rules — stale_progress, ' +
            'no_recent_logs and contract_expired_log — never fire at all, because they become true ' +
            'when time passes rather than when anybody edits anything.',
        /**
         * ⚠⚠ **TEN MINUTES, AND THE NUMBER IS ABOUT THE THREE TIME-BASED RULES RATHER THAN ABOUT
         * FRESHNESS.** Everything else the scan checks is already kept current by P5-4's on-read
         * reconcile, which fires the moment an audited change makes a project stale — so this chore is
         * not what keeps an open Alertek tab honest. What it is for is the projects nobody opens, and
         * the rules no edit can announce.
         *
         * ⚠️ **So the cadence is set by the BATCH, not by how quickly a finding should appear.** Ten
         * minutes at forty projects is 240 an hour: a workspace of fifty is scanned roughly every
         * quarter of an hour, one of a thousand about four times a day. A rule whose threshold is
         * fourteen WORKING DAYS does not care about the difference, which is the point — pick the
         * cadence that keeps the load flat, because the things it exists for age in weeks.
         */
        everyMs: 10 * 60_000,
    },
    {
        key: exports.INTEGRATION_MAIL,
        description: 'Tells the people who manage integrations when one has failed three runs in a row, once, ' +
            'until a run succeeds again (0189). If this stops, a broken sync is found by somebody ' +
            'wondering why their hours never reached Jira.',
        /** Fifteen minutes: the syncs themselves run on minutes, and it is one query per instance. */
        everyMs: 15 * 60_000,
    },
    {
        key: exports.FINANCE_MAIL,
        description: 'Sends each PM with money reports, on Monday at 09:00 their time, the closed periods and ' +
            'line items on their projects that still have revenue to invoice (0188). If this stops, ' +
            'finished work waits to be billed until somebody happens to look.',
        /** ⚠️ Hourly — one Monday morning a week needs no finer clock, and each is a money computation. */
        everyMs: 60 * 60_000,
    },
    {
        key: exports.LOGGING_MAIL,
        description: "Sends the time-logging emails and the weekly summary, each at the person's own time: the " +
            "daily reminder on a day with nothing logged, Friday's weekly gap and team gap, the warning " +
            "two working days before the month locks, and Monday's summary (0188). If this stops, " +
            'missing hours are found at the lock rather than before it.',
        /** ⚠️ Fifteen minutes — the reminder time is the person's, and this is its precision. */
        everyMs: 15 * 60_000,
    },
    {
        key: exports.INVITE_REMINDER,
        description: 'Reminds an invited address once, three days after the invitation, with a second link that ' +
            'expires with the first (0187). If this stops, an invitation lost in a mailbox simply ' +
            'expires.',
        /** ⚠️ Hourly — "three days" has no need of minutes, and the stamp makes it once. */
        everyMs: 60 * 60_000,
    },
    {
        key: exports.CONTRACT_WATCH,
        description: "Warns a contract's owner and its project's PM when it ends in 30 or 7 days, has ended but " +
            'is still active, has spent 80 % or all of its frame, or will run out before it ends (0186). ' +
            'If this stops, a contract runs past its end or its frame with nobody told.',
        /**
         * ⚠️ **HOURLY, BECAUSE EACH CONTRACT IS AN EFFORT COMPUTATION.** The frame figures are
         * `EffortService`'s, which reads every worklog and plan row on the contract — not a thing to do
         * every fifteen minutes for every active contract. Each warning goes at 09:00 in the owner's
         * zone, so an hour is the precision it needs; the ledger makes every later tick a no-op.
         */
        everyMs: 60 * 60_000,
    },
    {
        key: exports.ALERT_DIGEST,
        description: 'Sends each PM, once a day at their reminder time, the alerts that appeared on their ' +
            'projects since the last digest and are still open (0186). If this stops, new findings wait ' +
            'on the Alertek tab for somebody to open it.',
        /** ⚠️ Fifteen minutes — the due digest's precision, at the same reminder time. */
        everyMs: 15 * 60_000,
    },
    {
        key: exports.REQUEST_MAIL,
        description: 'Sends the leave and overtime emails that are due because time passed: the reminder to ' +
            'whoever a request has waited two working days for, the working day before leave starts, ' +
            'and the year-end allowance warning on 15 Nov and 1 Dec (0184). If this stops, requests ' +
            'wait in silence and days lapse at New Year without anybody being told.',
        /**
         * ⚠️ **FIFTEEN MINUTES, AND THAT IS THE DELIVERY PRECISION.** Each email goes at an hour in
         * the RECIPIENT's zone (09:00 for a reminder, 10:00 for the others), so a tick has to land soon
         * after that hour wherever they are. The ledger makes every tick after the first a no-op.
         */
        everyMs: 15 * 60_000,
    },
    {
        key: exports.EMAIL_AVATARS,
        description: "Keeps every member's email picture current — their uploaded photo, otherwise their " +
            'Blobatar, as a round PNG at a public address (0180). If this stops, emails still show ' +
            'faces: the message builder makes a missing one on the spot, it is only slower.',
        /**
         * ⚠️ **FIFTEEN MINUTES, AND NOTHING WAITS ON IT.** The builder generates a stale or missing
         * picture itself before it sends (`PublicAssetService.avatarUrl`), so this is the backfill and
         * the keeping-current, not the delivery path. Each tick is bounded at 50 people because
         * rendering is CPU, not I/O.
         */
        everyMs: 15 * 60_000,
    },
    {
        key: exports.DUE_SWEEP,
        description: 'Sends the due-date digest to anybody with a task due soon, in their own timezone. If this ' +
            'stops, nobody is warned about a deadline and nothing else reports it.',
        /**
         * ⚠️ **FIVE MINUTES IS THE DELIVERY PRECISION, NOT A POLL FOR WORK.** The sweep is idempotent
         * per person per local day (0029), so it does not need to fire ON somebody's reminder hour — it
         * needs to fire soon after it. Five minutes means a warning arrives within five minutes of
         * 16:30 in the recipient's own zone, and the other 287 ticks of the day match nobody and cost
         * one indexed query.
         */
        everyMs: 5 * 60_000,
    },
    {
        key: exports.LEAVE_CARRY_OVER,
        description: "Moves last year's unused leave into this year's balance, capped by each type's carry-over " +
            'rule, and stamps the date it expires. If this stops, a *max5* type is a sentence in the ' +
            'settings that describes nothing: the days are simply lost at midnight on 31 December.',
        /**
         * ⚠️⚠️ **DAILY, AND IT IS A CORRECTION LOOP RATHER THAN A YEAR-END EVENT.** The obvious design
         * — run once on 1 January — is the one that fails silently: the process may be down that
         * night, next year's quotas are usually typed in during January, and a backdated request
         * approved in February changes a remainder that was already carried. This rewrites its own
         * rows from what is true today, so a late start, a late quota and a late correction all land
         * on the next tick instead of needing somebody to notice.
         */
        everyMs: 24 * 60 * 60_000,
    },
    {
        key: exports.DUE_SWEEP_PRUNE,
        description: 'Trims the digest ledger, which is only ever read for today. Harmless if it is late; the ' +
            'table grows until it runs.',
        everyMs: 24 * 60 * 60_000,
    },
    {
        key: exports.TRIAL_EXPIRY,
        description: 'Suspends tenants whose trial has ended. If this stops, an expired trial keeps working and ' +
            'the app says something untrue about the account.',
        /**
         * ⚠️ **HOURLY, NOT DAILY, AND THE INTERVAL IS AGAIN THE DELIVERY PRECISION.** `expire_trials`
         * is one indexed statement over `tenants` where `status = 'trial'`, so 24 of them a day costs
         * nothing — and a daily tick means a trial that ended at 09:00 keeps working until whenever the
         * process last restarted. An hour is the largest window in which *"your trial has ended"* is
         * still an honest thing for the app to say.
         */
        everyMs: 60 * 60_000,
    },
    {
        key: exports.INTEGRATION_OVERWATCH,
        description: 'Reconciles every connected tracker: issues in, hours in, deletions, people, field options. ' +
            'If this stops, nothing that changes in Jira reaches Forecastify until somebody presses Sync ' +
            'now — and nobody is told.',
        /**
         * ⚠️⚠️ **HOURLY, AND THE RUN ITSELF IS CAPPED AT DAILY.** This is not a contradiction and the
         * two numbers do different jobs. `jira.md` §9.4 wants the cap *"in the schedule AND in the
         * runner"*; the runner refuses anything inside twenty-three hours of its last reconciliation,
         * so 23 of these 24 ticks are one indexed read and a refusal.
         *
         * ⚠️ **What the hourly offer buys is WHICH hour it lands on.** A workspace picks an
         * `overwatch_at`; a daily tick would run whenever this process last restarted instead, which
         * is 3am for one deployment and the middle of the working day for the next.
         */
        everyMs: 60 * 60_000,
    },
    {
        key: exports.INTEGRATION_OUTBOUND,
        description: 'Sends what was written here up to the tracker: hours, new issues, status transitions and ' +
            'mapped fields. If this stops, the work stays in Forecastify and the badge on the row says ' +
            'it is waiting — visible, but only to whoever looks at that row.',
        /**
         * ⚠️ **FIVE MINUTES, WHICH IS §9.3'S OWN NUMBER**: *"one request per pending item, every few
         * minutes, and it stops when the queue is empty."* It is explicitly NOT bound by the
         * overwatch's daily cap — that limits how often we download a client's Jira, and this pushes
         * writes we have already computed. A tick with an empty queue costs one indexed query.
         */
        everyMs: 5 * 60_000,
    },
    {
        key: exports.INTEGRATION_POLL,
        description: 'Reads what changed in a tracker that cannot tell us on its own — YouTrack has no webhooks ' +
            'at all. If this stops, nothing breaks and nothing is lost: changes arrive with the daily ' +
            'reconciliation instead, which means up to a day late rather than a few minutes.',
        /**
         * ⚠️⚠️ **FIVE MINUTES, AND `youtrack.md` §8.2 IS WHY THIS CHORE EXISTS AT ALL.** P2-24
         * withdrew the fifteen-minute poll because C39's webhook replaced it — and applying that
         * literally to a provider with **no webhook and no prospect of one** (§1.6) makes YouTrack
         * silently 24-hour latent: a change made over there at 09:00 appears here tomorrow, which nobody
         * decided and which no screen would say. The daily cap belongs to the RECONCILIATION, not to
         * reading.
         *
         * ⚠️⚠️ **AND THE NUMBER IS A DECISION RATHER THAN A DEFAULT.** §1.7: JetBrains publishes no
         * rate limit at all, so there is no budget to be frugal toward and no `Retry-After` to obey —
         * which argues for a conservative interval a workspace can lengthen rather than shorten, and for
         * the read being narrow by construction so that being wrong about the interval costs requests
         * rather than a client's instance. The product owner chose five minutes on 3 Sep 2026.
         *
         * ⚠️ **A tick against a provider that DOES push costs one no-op call**, because
         * `changesSince` yields nothing there. That is the same trade the overwatch already makes when
         * 23 of its 24 hourly fan-outs resolve to `skipped`.
         */
        everyMs: 5 * 60_000,
    },
    {
        key: exports.BLOB_SWEEP,
        description: 'Deletes uploaded files nothing points at any more — a removed attachment, a replaced ' +
            'profile picture — and frees the disk they were using. If this stops, nothing breaks; ' +
            'the volume grows until it runs.',
        /**
         * ⚠️ **SIX HOURS, BECAUSE NOTHING IS WAITING ON IT.** Every other cadence here is a delivery
         * precision — how late a person may hear about something. Nobody is told anything by this: it
         * reclaims disk, and disk reclaimed six hours late is disk reclaimed. The interval is set by
         * what it costs instead, which is one indexed anti-join per tick.
         */
        everyMs: 6 * 60 * 60_000,
    },
];
/** The keys, for validating what an operator asks to run. */
/**
 * ⚠⚠ **HOW MANY PROJECTS ONE TICK SCANS, AND IT IS SHARED FOR THE SAME REASON THE CADENCE IS.**
 * The pair *(every 10 minutes, 40 projects)* is a throughput — 240 projects an hour — and a
 * monitor that wants to say *"this workspace is scanned about twice a day"* needs both numbers.
 * One of them living in the API and the other here is how that sentence becomes wrong.
 *
 * ⚠️ **Forty because one project's scan reads roughly what the finance dashboard reads.** It is
 * eleven queries over tasks, worklogs, line items and allocations, so this is a real unit of work
 * rather than a row fetch — and the bound is what stops one enormous workspace starving the rest.
 */
exports.ALERT_SCAN_BATCH = 40;
exports.CHORE_KEYS = exports.CHORES.map((chore) => chore.key);
function isChoreKey(value) {
    return exports.CHORE_KEYS.includes(value);
}
function choreDefinition(key) {
    return exports.CHORES.find((chore) => chore.key === key);
}
/**
 * The cadence, for the module that schedules it.
 *
 * ⚠️ **It throws rather than defaulting.** A chore the worker schedules on a fallback interval is
 * one whose cadence the monitor and the timer disagree about, which is the drift this file exists to
 * remove — and a missing entry is a typo at startup, not a runtime condition to tolerate.
 */
function choreCadence(key) {
    const found = choreDefinition(key);
    if (found === undefined) {
        throw new Error(`no chore registered as "${key}" — add it to CHORES in @forecastify/shared, or the job ` +
            'monitor cannot describe it (decisions.md S-14)');
    }
    return found.everyMs;
}
//# sourceMappingURL=chores.js.map